import { CommunityFeature } from "@upsquad/shared";
import { Calendar, CalendarDays, MessageSquare, Rss, Users, Video } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Switch } from "@/src/common/components/ui";

interface FeatureTogglesProps {
	value: CommunityFeature[];
	onChange: (features: CommunityFeature[]) => void;
}

const FEATURE_CONFIG: {
	feature: CommunityFeature;
	icon: typeof MessageSquare;
	labelKey: "feature_messages" | "feature_feed" | "feature_members" | "feature_meetings" | "feature_events" | "feature_videos";
	descKey:
		| "feature_messages_desc"
		| "feature_feed_desc"
		| "feature_members_desc"
		| "feature_meetings_desc"
		| "feature_events_desc"
		| "feature_videos_desc";
}[] = [
	{ feature: CommunityFeature.MESSAGES, icon: MessageSquare, labelKey: "feature_messages", descKey: "feature_messages_desc" },
	{ feature: CommunityFeature.FEED, icon: Rss, labelKey: "feature_feed", descKey: "feature_feed_desc" },
	{ feature: CommunityFeature.MEMBERS, icon: Users, labelKey: "feature_members", descKey: "feature_members_desc" },
	{ feature: CommunityFeature.MEETINGS, icon: Calendar, labelKey: "feature_meetings", descKey: "feature_meetings_desc" },
	{ feature: CommunityFeature.EVENTS, icon: CalendarDays, labelKey: "feature_events", descKey: "feature_events_desc" },
	{ feature: CommunityFeature.VIDEOS, icon: Video, labelKey: "feature_videos", descKey: "feature_videos_desc" },
];

export function FeatureToggles({ value, onChange }: FeatureTogglesProps) {
	const { t } = useTranslation("onboarding");

	const handleToggle = (feature: CommunityFeature, enabled: boolean) => {
		if (enabled) {
			onChange([...value, feature]);
		} else {
			onChange(value.filter((f) => f !== feature));
		}
	};

	return (
		<div>
			<div className="flex items-baseline justify-between mb-3">
				<p className="text-sm font-medium">{t("features_label")}</p>
				<p className="text-xs text-foreground/40">{t("features_hint")}</p>
			</div>
			<div className="flex flex-col gap-2 max-h-[300px] overflow-y-auto pr-1">
				{FEATURE_CONFIG.map(({ feature, icon: Icon, labelKey, descKey }) => {
					const isEnabled = value.includes(feature);

					return (
						<div key={feature} className="flex items-center justify-between p-3 rounded-xl border border-default-200 bg-content1">
							<div className="flex items-center gap-3">
								<div className="flex items-center justify-center size-8 rounded-lg bg-default-100">
									<Icon className="size-4 text-foreground/50" />
								</div>
								<div>
									<p className="text-sm font-medium">{t(labelKey)}</p>
									<p className="text-xs text-foreground/40">{t(descKey)}</p>
								</div>
							</div>
							<Switch isSelected={isEnabled} onChange={(selected) => handleToggle(feature, selected)} size="sm">
								<Switch.Control>
									<Switch.Thumb />
								</Switch.Control>
							</Switch>
						</div>
					);
				})}
			</div>
		</div>
	);
}
